import { useCallback, useContext } from 'react';
import type { ViewSnapshot } from '@api/apps';
import { trackAction, trackError } from '@utils/datadog';
import { CaptureFailure, captureView } from './captureView';
import { ViewSnapshotContext } from './viewSnapshotContext';

export interface ViewSnapshotOptions {
  /** Which screen is asking, for the failure report. */
  surface: 'app_run' | 'app_builder';
  appKey: string | undefined;
}

const reasonOf = (error: unknown) => (error instanceof CaptureFailure ? error.reason : 'error');

const decodedBytes = (base64: string) =>
  Math.floor((base64.length * 3) / 4) - (base64.match(/=*$/)?.[0].length ?? 0);

/**
 * Screenshots of the screen under the nearest {@link ViewSnapshotProvider}.
 * `capture` never rejects: a screenshot is an extra, so a failure is reported
 * and the message goes without one.
 */
export const useViewSnapshot = ({ surface, appKey }: ViewSnapshotOptions) => {
  const registry = useContext(ViewSnapshotContext);

  const capture = useCallback(async (): Promise<ViewSnapshot | null> => {
    const root = registry?.rootRef.current;
    if (!registry || !root) return null;
    const sources = [...registry.sources];
    const started = performance.now();
    try {
      const snapshot = await captureView(root, sources);
      trackAction('app_view_snapshot', {
        surface,
        durationMs: Math.round(performance.now() - started),
        bytes: decodedBytes(snapshot.base64),
      });
      return snapshot;
    } catch (error) {
      trackError(new Error('app_view_snapshot_failed'), {
        reason: reasonOf(error),
        surface,
        viewKinds: sources.map((source) => source.kind),
        durationMs: Math.round(performance.now() - started),
        appKey,
      });
      return null;
    }
  }, [registry, surface, appKey]);

  return { rootRef: registry?.rootRef, capture };
};

/** The element screenshots are taken of; undefined outside a provider. */
export const useSnapshotRoot = () => useContext(ViewSnapshotContext)?.rootRef;
